import * as React from 'react'
import { Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import type { Metric, MetricUnit, PeriodKind } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'

const UNITS: Array<{ value: MetricUnit; label: string }> = [
  { value: 'points', label: 'Points' },
  { value: 'percent', label: 'Percent' },
  { value: 'number', label: 'Plain number' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'currency', label: 'Currency' },
  { value: 'ratio', label: 'Ratio (×)' },
]

const PERIODS: Array<{ value: PeriodKind; label: string }> = [
  { value: 'sprint', label: 'Per sprint' },
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
  { value: 'quarter', label: 'Per quarter' },
  { value: 'adhoc', label: 'Ad hoc' },
]

/** Templates for the metrics an accelerator almost always wants. */
const PRESETS: Array<Partial<Metric> & { label: string }> = [
  { label: 'Points / Sprint', key: 'velocity', unit: 'points', direction: 'up', period_kind: 'sprint' },
  { label: 'Code Coverage', key: 'code_coverage', unit: 'percent', direction: 'up', period_kind: 'sprint', target: 80 },
  { label: 'System Coverage', key: 'system_coverage', unit: 'percent', direction: 'up', period_kind: 'sprint', target: 70 },
  { label: 'Defects / Sprint', key: 'defects', unit: 'number', direction: 'down', period_kind: 'sprint' },
  { label: 'Cycle Time', key: 'cycle_time', unit: 'days', direction: 'down', period_kind: 'sprint' },
]

export function MetricEditor({
  teamId,
  metric,
  onClose,
  onSaved,
}: {
  teamId: string
  metric?: Metric
  onClose: () => void
  onSaved: () => void
}) {
  const [label, setLabel] = React.useState(metric?.label ?? '')
  const [key, setKey] = React.useState(metric?.key ?? '')
  const [description, setDescription] = React.useState(metric?.description ?? '')
  const [unit, setUnit] = React.useState<MetricUnit>(metric?.unit ?? 'points')
  const [direction, setDirection] = React.useState<'up' | 'down'>(metric?.direction ?? 'up')
  const [periodKind, setPeriodKind] = React.useState<PeriodKind>(metric?.period_kind ?? 'sprint')
  const [target, setTarget] = React.useState(metric?.target != null ? String(metric.target) : '')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ label, key, description, unit, direction, periodKind, target })
  const guard = useDiscardGuard(dirty, onClose)

  // How many readings would go with it. Deleting a metric cascades its
  // metric_values (0002), so the count is the whole content of the warning —
  // "delete this metric" and "delete four sprints of history" are different
  // decisions and only the number tells them apart. Null until it is known, so
  // the button can say nothing rather than guess.
  const [readings, setReadings] = React.useState<number | null>(null)
  React.useEffect(() => {
    if (!metric) return
    let cancelled = false
    void (async () => {
      const { count } = await sb()
        .from('metric_values')
        .select('id', { count: 'exact', head: true })
        .eq('metric_id', metric.id)
      if (!cancelled) setReadings(count ?? 0)
    })()
    return () => {
      cancelled = true
    }
  }, [metric])

  async function remove() {
    if (!metric) return
    const n = readings ?? 0
    const warning =
      n > 0
        ? `Delete “${metric.label}” and its ${n} recorded ${n === 1 ? 'reading' : 'readings'}?\n\n` +
          'The readings are deleted with it and cannot be recovered.'
        : `Delete “${metric.label}”?`
    if (!window.confirm(warning)) return

    setBusy(true)
    setError(null)
    try {
      const { error: err } = await sb().from('metrics').delete().eq('id', metric.id)
      if (err) throw err
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Only auto-derive the key while creating; changing it on an existing metric
  // would orphan nothing, but the key is how a team recognizes the metric.
  const keyTouched = React.useRef(Boolean(metric))
  function applyLabel(next: string) {
    setLabel(next)
    if (!keyTouched.current) setKey(slugify(next))
  }

  function applyPreset(p: (typeof PRESETS)[number]) {
    setLabel(p.label)
    setKey(p.key ?? slugify(p.label))
    setUnit(p.unit ?? 'number')
    setDirection(p.direction ?? 'up')
    setPeriodKind(p.period_kind ?? 'sprint')
    setTarget(p.target != null ? String(p.target) : '')
    keyTouched.current = true
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!label.trim()) return setError('Give the metric a name.')
    const k = (key.trim() || slugify(label)).toLowerCase()
    const targetNum = target.trim() === '' ? null : Number(target)
    if (targetNum != null && !Number.isFinite(targetNum)) return setError('Target must be a number.')

    setBusy(true)
    setError(null)
    try {
      const row = {
        team_id: teamId,
        key: k,
        label: label.trim(),
        description: description.trim() || null,
        unit,
        direction,
        period_kind: periodKind,
        target: targetNum,
      }
      const { error: err } = metric
        ? await sb().from('metrics').update(row).eq('id', metric.id)
        : await sb().from('metrics').insert(row)
      if (err) throw err
      onSaved()
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err)
      setError(
        /duplicate key|unique/i.test(raw)
          ? `This team already has a metric with the key “${k}”.`
          : raw,
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this metric" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{metric ? 'Edit metric' : 'New metric'}</DialogTitle>
            <DialogDescription>
              Defines what the team tracks. Readings are recorded against it each period.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">
            {!metric && (
              <div>
                <Label className="text-xs text-muted-foreground">Start from a common one</Label>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <Button
                      key={p.key}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => applyPreset(p)}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="label">Name</Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => applyLabel(e.target.value)}
                placeholder="Points / Sprint"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="unit">Unit</Label>
                <Select value={unit} onValueChange={(v) => setUnit(v as MetricUnit)}>
                  <SelectTrigger id="unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cadence">Cadence</Label>
                <Select value={periodKind} onValueChange={(v) => setPeriodKind(v as PeriodKind)}>
                  <SelectTrigger id="cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="direction">Improvement is</Label>
                <Select value={direction} onValueChange={(v) => setDirection(v as 'up' | 'down')}>
                  <SelectTrigger id="direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="up">Higher is better</SelectItem>
                    <SelectItem value="down">Lower is better</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Controls which way green points.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="target">Target (optional)</Label>
                <Input
                  id="target"
                  inputMode="decimal"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder="—"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="How this is measured, so everyone records it the same way"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          {/* Delete sits apart from Cancel/Save, on the other end of the row:
              it is the one button here that destroys data, and it should not be
              adjacent to the one people press by reflex. Only offered on an
              existing metric — RLS refuses it from anyone but a lead. */}
          <DialogFooter className="sm:justify-between">
            {metric ? (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => void remove()}
                disabled={busy}
              >
                {/* The label stays short — the footer is 512px wide and shares
                    it with Cancel and Save. The reading count belongs in the
                    confirm, where there is room to say what it means. */}
                <Trash2 />
                <span className="whitespace-nowrap">Delete</span>
              </Button>
            ) : (
              <span />
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {metric ? 'Save changes' : 'Create metric'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
}
